/* facet-drawer-live-update.js
   ----------------------------------------------------------------
   Make the mobile / horizontal-layout filter drawer behave like
   the desktop sidebar in real time: as the shopper toggles
   checkboxes, refresh:
   - filter value counts ("(12)" → "(8)")
   - the apply button label ("Show 234 results" → "Show 58 results")
   - hide filters that have become irrelevant for the current set
     (same logic the snippet already runs server-side — we just
     re-render the snippet against the new URL)
   …WITHOUT submitting the form or closing the drawer. Submission
   still happens on the "Show X results" tap (the existing
   FacetApplyButton._closeDrawer → FacetDrawer.dialog:after-hide →
   form.requestSubmit chain).

   Why a new module instead of `update-on-change`:
   - `<facet-form update-on-change>` (the desktop sidebar mode)
     dispatches the theme's `facet:update` event, which replaces
     the ENTIRE shopify-section with new markup. That swap
     destroys the open drawer element (and re-renders the
     products underneath unnecessarily). We need a surgical
     in-drawer swap that preserves drawer state.

   Mechanism:
   1. On every `change` event in a drawer-mode `<facet-form>`
      (one without `update-on-change`), debounce 200ms and fetch
      the current section against the SAME URL that submission
      would build, plus `section_id` so Shopify returns just our
      section's markup.
   2. Parse the response, find the corresponding `<facet-form>`
      inside the response's `<facet-drawer>`, and `replaceChildren`
      into our live form — same form element preserved (so the
      `<facet-form>` custom-element instance and its listeners
      stay attached), only the inner content (accordions + apply
      button) is replaced.
   3. Preserve `<details open>` state across the swap so an
      accordion the user was working in stays expanded.

   What NOT to update on each change:
   - Product grid (would change underneath the drawer overlay).
   - Active facets row at the top of the page (only meaningful
     once the user actually applies).
   - URL (page navigation happens on submit, not on toggle).

   Race handling:
   - AbortController cancels in-flight fetches when a newer
     change arrives. Debounce smooths rapid clicks into a single
     request. With both, only the final state propagates.

   The script is a no-op for `<facet-form update-on-change>` (the
   desktop sidebar), which already does its own thing via
   `facet:update`.
*/
(function () {
  /* Small debounce just to coalesce same-burst clicks (e.g. a
     swatch group where the user taps two values 30ms apart). Larger
     values felt laggy — the merchant flagged the apply button as
     "not updating" while it was actually waiting on debounce + fetch
     round-trip. 50ms is small enough to feel instant for single
     toggles. */
  const DEBOUNCE_MS = 50;

  function init() {
    /* The drawer is rendered in the section but moved to <body>
       when opened. Either way, the form element travels with it,
       so attaching listeners by `#facet-form` works in both
       states. We attach to ALL drawer-mode forms on the page —
       collection and search both render a facets-drawer with the
       same `#facet-form` id; querySelectorAll handles either. */
    document.querySelectorAll('form#facet-form').forEach(wire);
  }

  function wire(form) {
    if (form.hasAttribute('update-on-change')) return; /* desktop sidebar; let the theme handle it */
    if (form.dataset.liveUpdateBound) return;
    form.dataset.liveUpdateBound = '1';

    let abortController = null;
    let debounceTimer = null;

    form.addEventListener('change', () => {
      /* Immediate visual feedback: dim the apply button so the user
         knows the count is being recalculated. Removed once the
         fetch returns and the button is re-rendered. */
      markApplyLoading(form, true);

      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => updateDrawer(form), DEBOUNCE_MS);
    });

    async function updateDrawer(form) {
      if (abortController) abortController.abort();
      const myController = new AbortController();
      abortController = myController;

      const url = buildUrl(form);

      try {
        const response = await fetch(url.toString(), { signal: myController.signal });
        if (!response.ok) return;
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');

        /* Find the form in the response. Both collection and search
           render the drawer with a child form#facet-form, so the
           same selector works. */
        const newForm = doc.querySelector('facet-drawer #facet-form');
        if (!newForm) return;

        /* ---- Surgical swap ----------------------------------------
           Earlier we used `form.replaceChildren(...newForm.children)`.
           That works visually, but it REMOVES the old
           <facet-apply-button> custom element and INSERTS a fresh
           one. Each fresh FacetApplyButton constructor attaches a
           NEW `change` listener on the form — and the removed
           buttons' listeners are never cleaned up. After a few
           rapid toggles, you accumulate dozens of stale listeners
           all firing `new FormData(this.form)` on a detached
           button (whose .form getter returns null), producing
           console errors:
             Uncaught TypeError: Failed to construct 'FormData':
             parameter 1 is not of type 'HTMLFormElement'.

           Fix: preserve the ORIGINAL apply button across swaps.
           Only its TEXT changes (the "Show X results" label), so
           we pull the new label out of the parsed response and
           update the old button's text-node in place. Everything
           else (accordions, hidden inputs, sort-by row) gets the
           normal replaceChildren treatment.
        */
        const oldApplyContainer = form.querySelector('.facets-drawer__floating-apply');
        const newApplyContainer = newForm.querySelector('.facets-drawer__floating-apply');
        let newApplyLabel = null;
        if (newApplyContainer) {
          const newBtn = newApplyContainer.querySelector('[is="facet-apply-button"]');
          /* DOMParser doesn't run custom-element constructors, so
             the new button's textContent is the raw server-rendered
             label "Show X results" (no auto-appended count span). */
          if (newBtn) newApplyLabel = newBtn.textContent.trim();
          /* Remove from the parsed tree so replaceChildren below
             doesn't try to insert a duplicate. */
          newApplyContainer.remove();
        }

        /* Preserve which accordions were open so the user's
           current context (e.g. mid-scroll inside an open
           "Color" filter) survives the swap. */
        const openAccordionIds = new Set();
        form.querySelectorAll('details[open]').forEach((d) => {
          if (d.id) openAccordionIds.add(d.id);
        });

        /* Detach the existing apply container from the form so it
           doesn't get wiped by replaceChildren. */
        if (oldApplyContainer) oldApplyContainer.remove();

        /* Replace everything else (filters, accordions, hidden
           inputs, optional sort-by row). */
        form.replaceChildren(...newForm.children);

        /* Restore accordion open state. */
        openAccordionIds.forEach((id) => {
          const el = form.querySelector('#' + CSS.escape(id));
          if (el) el.setAttribute('open', '');
        });

        /* Update the preserved apply button's label text in place,
           then re-attach the container to the form. */
        if (oldApplyContainer) {
          if (newApplyLabel) {
            const oldBtn = oldApplyContainer.querySelector('[is="facet-apply-button"]');
            if (oldBtn) {
              /* FacetApplyButton.constructor put two children on the
                 button: a text node ("Show X results") and an
                 auto-appended <span> with " (N)". Update only the
                 text node; leave the span alone (it's hidden by CSS
                 anyway and is kept in sync by FacetApplyButton's
                 own change-listener). */
              const firstText = Array.from(oldBtn.childNodes).find((n) => n.nodeType === 3);
              if (firstText) {
                firstText.nodeValue = newApplyLabel;
              } else {
                oldBtn.insertBefore(document.createTextNode(newApplyLabel), oldBtn.firstChild);
              }
            }
          }
          form.appendChild(oldApplyContainer);
        }
      } catch (e) {
        if (e.name === 'AbortError') return;
        /* Network failure / non-200: leave the existing drawer
           content in place. The user can still apply with the
           stale-but-functional state and the next navigation
           will sync everything up. */
      } finally {
        /* Clear loading state ONLY if this fetch was still the
           active one. A subsequent toggle would have replaced
           `abortController` with its own controller; in that case
           leaving the loading class on is correct (the new fetch
           hasn't completed yet). Without this guard, an aborted
           fetch's finally would prematurely clear the class while
           the user is still waiting for the latest result. */
        if (abortController === myController) {
          markApplyLoading(form, false);
        }
      }
    }
  }

  function markApplyLoading(form, on) {
    form.classList.toggle('is-live-updating', !!on);
  }

  /* Mirrors FacetForm._buildUrl in theme.js. We don't reuse it
     directly because it's a private method on a class instance
     that we don't have a clean handle on from outside. */
  function buildUrl(form) {
    const url = new URL(form.action, window.location.origin);
    url.search = '';
    const data = new URLSearchParams(new FormData(form));
    data.forEach((value, key) => url.searchParams.append(key, value));
    /* Strip empty-value params the way the theme does — these
       leak into the URL because of how the form serializes empty
       hidden inputs / unset price range fields. */
    ['page', 'filter.v.price.gte', 'filter.v.price.lte'].forEach((k) => {
      if (url.searchParams.get(k) === '') url.searchParams.delete(k);
    });
    url.searchParams.set('section_id', form.getAttribute('section-id'));
    return url;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
